import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MOCK_CICLO } from '../dados-simulados';
import {
  MOCK_MEDICOES,
  MOCK_MEDICAO_ITENS,
  MOCK_MEDICAO_AJUSTES,
  MOCK_MEDICAO_OBSERVACOES,
  MOCK_MEDICAO_SOF_TIMELINE,
  MOCK_MEDICAO_REVISOES,
  MOCK_MEDICAO_PEDIDOS_PORTAL,
  MOCK_MEDICAO_DOCUMENTOS,
  MOCK_MEDICAO_CHECKLIST,
  MOCK_MEDICAO_CAPACIDADE,
  MOCK_MEDICAO_HISTORICO,
} from '../dados-simulados';

export interface FiltroMedicoes {
  mes?: string;
  status?: string;
  page: number;
  perPage: number;
}

/** FIN-XXX: lista de medições mensais por credenciado — usada standalone e como
 * aba "Medições" do hub de Apuração. Reaproveita MOCK_CICLO (mesma entidade,
 * outra visão) até a API própria de medições existir. */
export function useMedicoesLista(filtro: FiltroMedicoes) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', 'lista', filtro],
    queryFn: async () => {
      let filtrados = [...MOCK_CICLO];
      if (filtro.mes) {
        filtrados = filtrados.filter((m) => m.mesReferencia.startsWith(filtro.mes!));
      }
      if (filtro.status) {
        filtrados = filtrados.filter((m) => m.status === filtro.status);
      }
      return {
        dados: filtrados,
        total: filtrados.length,
        page: filtro.page,
        perPage: filtro.perPage,
      };
    },
    placeholderData: (dadosAnteriores) => dadosAnteriores,
  });
}

/** FIN-002: cabeçalho/resumo de uma medição específica. */
export function useMedicaoDetalhe(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id],
    queryFn: async () => MOCK_MEDICOES.find((m) => m.id === id) ?? null,
    enabled: id.length > 0,
  });
}

export function useMedicaoItens(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'itens'],
    queryFn: async () => MOCK_MEDICAO_ITENS[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoAjustes(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'ajustes'],
    queryFn: async () => MOCK_MEDICAO_AJUSTES[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoObservacoes(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'observacoes'],
    queryFn: async () => MOCK_MEDICAO_OBSERVACOES[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoSofTimeline(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'sof'],
    queryFn: async () => MOCK_MEDICAO_SOF_TIMELINE[id] ?? null,
    enabled: id.length > 0,
  });
}

export function useMedicaoRevisoes(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'revisoes'],
    queryFn: async () => MOCK_MEDICAO_REVISOES[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoPedidosPortal(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'portal'],
    queryFn: async () => MOCK_MEDICAO_PEDIDOS_PORTAL[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoDocumentos(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'documentos'],
    queryFn: async () => MOCK_MEDICAO_DOCUMENTOS[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoChecklist(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'checklist'],
    queryFn: async () => MOCK_MEDICAO_CHECKLIST[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoCapacidade(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'capacidade'],
    queryFn: async () => MOCK_MEDICAO_CAPACIDADE[id] ?? [],
    enabled: id.length > 0,
  });
}

export function useMedicaoHistorico(id: string) {
  return useQuery({
    queryKey: ['financeiro', 'medicoes', id, 'historico'],
    queryFn: async () => MOCK_MEDICAO_HISTORICO[id] ?? [],
    enabled: id.length > 0,
  });
}

/** Ações de escrita das abas da medição — todas simuladas (latência + invalidação),
 * no mesmo padrão de `useAtualizarMovimento` (useApuracao.ts). */
export function useMutacoesMedicao(id: string) {
  const queryClient = useQueryClient();

  const invalidar = (sufixo: string) =>
    queryClient.invalidateQueries({ queryKey: ['financeiro', 'medicoes', id, sufixo] });

  const simular = async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return { ok: true as const };
  };

  return {
    novaRevisao: useMutation({
      mutationFn: simular,
      onSettled: () => void invalidar('revisoes'),
    }),
    responderPedidoPortal: useMutation({
      mutationFn: simular,
      onSettled: () => void invalidar('portal'),
    }),
    enviarDocumento: useMutation({
      mutationFn: simular,
      onSettled: () => void invalidar('documentos'),
    }),
    atualizarChecklist: useMutation({
      mutationFn: simular,
      onSettled: () => void invalidar('checklist'),
    }),
    editarInformacoesDemonstrativo: useMutation({
      mutationFn: simular,
      onSettled: () => void invalidar(''),
    }),
  };
}
